import type { ReactNode } from 'react';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { formatDateTime, formatMoney } from '@/utils/format';
import type { SinglePaymentChannel, Transaction } from '@/types/portal';
import { originMeta } from './originMeta';

interface TransactionDetailModalProps {
  open: boolean;
  row: Transaction | null;
  onClose: () => void;
  onOpenSource: (row: Transaction) => void;
  onOpenInvoice: (invoiceNumber: string) => void;
}

const channelLabels: Record<SinglePaymentChannel, string> = {
  wallet: 'Wallet-to-wallet',
  mobile: 'Mobile money',
  bank: 'Bank transfer',
  bill: 'Paybill',
  airtime: 'Airtime',
  goods: 'Buy Goods',
};

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-xs font-label font-medium uppercase tracking-wider text-foreground-500">
        {label}
      </dt>
      <dd className="min-w-0 text-right text-sm text-foreground-900">{children}</dd>
    </div>
  );
}

export default function TransactionDetailModal({
  open,
  row,
  onClose,
  onOpenSource,
  onOpenInvoice,
}: TransactionDetailModalProps) {
  if (!row) return null;
  const origin = originMeta[row.origin];
  const isCredit = row.direction === 'Credit';
  const invoiceNumber = row.relatedRef?.startsWith('INV-') ? row.relatedRef : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={row.reference}
      subtitle={formatDateTime(row.createdAt)}
      icon={isCredit ? 'ri-arrow-down-circle-line' : 'ri-arrow-up-circle-line'}
      widthClassName="max-w-xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button icon="ri-arrow-right-up-line" onClick={() => onOpenSource(row)}>
            {origin.sourceLabel}
          </Button>
        </>
      }
    >
      <div className="rounded-md border border-background-200 bg-background-100 px-5 py-4 text-center">
        <p className="text-xs font-label font-medium uppercase tracking-wider text-foreground-500">
          {isCredit ? 'Money in' : 'Money out'}
        </p>
        <p
          className={`mt-1 font-heading text-3xl font-semibold ${
            isCredit ? 'text-accent-700' : 'text-foreground-950'
          }`}
        >
          {isCredit ? '+' : '−'}
          {formatMoney(row.amount, row.currency)}
        </p>
        <div className="mt-2 flex items-center justify-center gap-2">
          <Badge tone={origin.tone} icon={origin.icon}>
            {origin.label}
          </Badge>
          <Badge tone={row.recon === 'Reconciled' ? 'accent' : 'secondary'}>{row.recon}</Badge>
        </div>
      </div>

      <dl className="mt-4 divide-y divide-background-100">
        <DetailRow label="Status">{row.status}</DetailRow>
        <DetailRow label="Recipient">
          {row.party}
          <span className="block text-xs text-foreground-500">{row.partyType}</span>
        </DetailRow>
        <DetailRow label="Description">{row.description}</DetailRow>
        {row.channel ? <DetailRow label="Channel">{channelLabels[row.channel]}</DetailRow> : null}
        {row.batchSize ? (
          <DetailRow label="Batch size">
            {row.batchSize} recipient{row.batchSize === 1 ? '' : 's'}
          </DetailRow>
        ) : null}
        <DetailRow label="Wallet">{row.wallet}</DetailRow>
        <DetailRow label="Rail">{row.rail}</DetailRow>
        {row.campaign ? <DetailRow label="Campaign">{row.campaign}</DetailRow> : null}
        {invoiceNumber ? (
          <DetailRow label="Invoice">
            <button
              type="button"
              onClick={() => onOpenInvoice(invoiceNumber)}
              className="cursor-pointer font-medium text-primary-700 hover:text-primary-900"
            >
              {invoiceNumber}
              <i className="ri-arrow-right-s-line ml-0.5" />
            </button>
          </DetailRow>
        ) : row.relatedRef ? (
          <DetailRow label="Related">
            <span className="font-mono text-xs text-foreground-700">{row.relatedRef}</span>
          </DetailRow>
        ) : null}
      </dl>
    </Modal>
  );
}